import { Download, Expand, LocateFixed, Minus, Plus } from "@jurisui/core/icons";
import { Button } from "@jurisui/core/primitives/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@jurisui/core/primitives/tooltip";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

let initialized = false;
let mermaidPromise: Promise<typeof import("mermaid").default> | undefined;

async function loadMermaid() {
  mermaidPromise ??= import("mermaid").then(({ default: mermaid }) => {
    if (!initialized) {
      const themeRoot = document.querySelector(".live-docs-theme") ?? document.documentElement;
      const styles = getComputedStyle(themeRoot);
      const color = (token: string) => `hsl(${styles.getPropertyValue(token).trim()})`;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        suppressErrorRendering: true,
        htmlLabels: false,
        theme: "base",
        flowchart: {
          htmlLabels: false,
        },
        themeVariables: {
          background: color("--card"),
          primaryColor: color("--accent"),
          primaryTextColor: color("--foreground"),
          primaryBorderColor: color("--secondary"),
          lineColor: color("--muted-foreground"),
          secondaryColor: color("--muted"),
          tertiaryColor: color("--card"),
          fontFamily: styles.getPropertyValue("--font-ui").trim(),
        },
      });
      initialized = true;
    }
    return mermaid;
  });
  return mermaidPromise;
}

export async function preloadMermaid(): Promise<void> {
  await loadMermaid();
}

function cleanSvg(rendered: string) {
  const documentValue = new DOMParser().parseFromString(rendered, "image/svg+xml");
  if (documentValue.querySelector("parsererror")) throw new Error("Invalid SVG output");
  documentValue.querySelectorAll("script, foreignObject, a, image").forEach((element) => element.remove());
  documentValue.querySelectorAll("*").forEach((element) => {
    for (const attribute of [...element.attributes]) {
      if (/^on/i.test(attribute.name) || /^(?:href|xlink:href)$/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    }
  });
  const svg = documentValue.documentElement;
  svg.setAttribute("role", "img");
  svg.setAttribute("focusable", "false");
  return new XMLSerializer().serializeToString(svg);
}

function safeFileName(title: string, extension: string) {
  const stem = title
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase()
    .slice(0, 64) || "diagram";
  return `${stem}.${extension}`;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

async function svgAsPng(svg: string, title: string) {
  const parsed = new DOMParser().parseFromString(svg, "image/svg+xml").documentElement;
  const viewBox = parsed.getAttribute("viewBox")?.split(/\s+/).map(Number);
  const width = Math.max(320, Math.ceil(viewBox?.[2] || 1200));
  const height = Math.max(180, Math.ceil(viewBox?.[3] || 700));
  const scale = Math.min(2, 2400 / width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("PNG export is not supported in this browser");
  const themeRoot = document.querySelector(".live-docs-theme") ?? document.documentElement;
  const cardToken = getComputedStyle(themeRoot).getPropertyValue("--card").trim();
  context.fillStyle = cardToken ? `hsl(${cardToken})` : getComputedStyle(document.body).backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const image = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("The diagram could not be prepared for PNG export"));
    image.src = url;
  });
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const dataUrl = canvas.toDataURL("image/png");
  if (dataUrl === "data:,") throw new Error("PNG export failed");
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = safeFileName(title, "png");
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

export interface ProgressiveMermaidProps {
  title: string;
  description: string;
  source: string;
  completeToken: number;
}

function DiagramTool({
  label,
  disabled,
  onClick,
  children,
  text = false,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  text?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={text ? "diagram-tool diagram-tool-text" : "diagram-tool"}
          onClick={onClick}
          aria-label={label}
          disabled={disabled}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function ProgressiveMermaid({ title, description, source, completeToken }: ProgressiveMermaidProps) {
  const reactId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const figureRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState("");
  const [failed, setFailed] = useState(false);
  const [revealState, setRevealState] = useState<"loading" | "animating" | "complete">("loading");
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ pointerId: number; x: number; y: number; originX: number; originY: number }>();

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    setFailed(false);
    setSvg("");
    setRevealState("loading");
    void loadMermaid()
      .then(async (mermaid) => {
        const valid = await mermaid.parse(source, { suppressErrors: true });
        if (!valid) throw new Error("Invalid Mermaid source");
        return mermaid.render(`live-docs-${reactId}`, source);
      })
      .then(({ svg: rendered }) => {
        if (cancelled) return;
        setSvg(cleanSvg(rendered));
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        setRevealState(reduceMotion ? "complete" : "animating");
        if (!reduceMotion) timer = window.setTimeout(() => setRevealState("complete"), 2_800);
      })
      .catch(() => {
        document.getElementById(`live-docs-${reactId}`)?.remove();
        document.getElementById(`dlive-docs-${reactId}`)?.remove();
        if (!cancelled) {
          setFailed(true);
          setRevealState("complete");
        }
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [reactId, source]);

  useEffect(() => {
    if (revealState === "animating") setRevealState("complete");
    // completeToken intentionally ends a current reveal without restarting it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeToken]);

  function fit() {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }

  async function toggleFullscreen() {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await figureRef.current?.requestFullscreen();
  }

  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    drag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!drag.current || drag.current.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.current.originX + event.clientX - drag.current.x,
      y: drag.current.originY + event.clientY - drag.current.y,
    });
  }

  function pointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointerId === event.pointerId) drag.current = undefined;
  }

  if (failed) {
    return (
      <figure className="diagram-card diagram-fallback" data-testid="diagram-fallback">
        <figcaption className="diagram-fallback-caption">
          <strong>{title}</strong>
          <span>{description}</span>
        </figcaption>
        <p role="alert">This diagram could not be drawn. The explanation above is still available.</p>
      </figure>
    );
  }

  return (
    <figure
      className="diagram-card"
      ref={figureRef}
      data-testid="mermaid-diagram"
      data-reveal-state={revealState}
      aria-label={title}
    >
      <div className="diagram-heading">
        <figcaption>
          <strong>{title}</strong>
          <span>{description}</span>
        </figcaption>
        <div className="diagram-tools" aria-label={`${title} controls`}>
          <DiagramTool label={`Zoom out ${title}`} onClick={() => setZoom((value) => Math.max(0.5, value - 0.2))}>
            <Minus aria-hidden="true" />
          </DiagramTool>
          <output aria-label="Diagram zoom">{Math.round(zoom * 100)}%</output>
          <DiagramTool label={`Zoom in ${title}`} onClick={() => setZoom((value) => Math.min(3, value + 0.2))}>
            <Plus aria-hidden="true" />
          </DiagramTool>
          <DiagramTool label={`Fit ${title}`} onClick={fit}>
            <LocateFixed aria-hidden="true" />
          </DiagramTool>
          <DiagramTool label={`Fullscreen ${title}`} onClick={() => void toggleFullscreen()}>
            <Expand aria-hidden="true" />
          </DiagramTool>
          <DiagramTool
            label={`Download ${title} as SVG`}
            onClick={() => downloadBlob(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), safeFileName(title, "svg"))}
            disabled={!svg}
          >
            <Download aria-hidden="true" />
            <span className="sr-only">SVG</span>
          </DiagramTool>
          <DiagramTool
            label={`Download ${title} as PNG`}
            onClick={() => void svgAsPng(svg, title)}
            disabled={!svg}
            text
          >
            PNG
          </DiagramTool>
        </div>
      </div>
      <div
        className="diagram-viewport"
        ref={viewportRef}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
      >
        {svg ? (
          <div
            className={`diagram-stage ${revealState === "animating" ? "is-revealing" : ""}`}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})` }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div className="diagram-loading" role="status">Drawing diagram…</div>
        )}
      </div>
    </figure>
  );
}
